// Path: src/components/aols/aeo/OLSaeo/OLSaeo.tsx
// Updated to remove duplicate footer, fix nested link error, and enhance SEO for Sun Light Meditation education
// Ensures single footer rendering in PublicLayout.tsx
// 05/24/2024 5:00 PM
// Added OLS Developer and Promoter section, improved section separation, and included reference icons
// Path: src/components/aols/aeo/OLSaeo/OLSaeo.tsx
// Updated to fix Google Search Console structured data errors (missing category, repeatCount),
// enhance SEO with richer schema, internal links to /OLSStudentProspect and /referral,
// and optimize images for faster load and crawlability.
// Maintains single footer rendering in PublicLayout.tsx and adds lead generation CTAs.
// 05/26/2025 11:11 AM +07
// Path: src/components/aols/aeo/OLSaeo/OLSaeo.tsx
// Fixed Google Search Console structured data error by adding missing "category" field to Event schema offers
// Enhanced Event schema with startDate and endDate for better SEO
// Maintained SEO focus with keywords and lead generation CTAs
// 05/31/25 07:08 PM +07
// Path: src/components/aols/aeo/OLSaeo/OLSaeo.tsx
// Updated to fix Google Search Console structured data error by adding startDate to Event schema
// Ensures single footer rendering in PublicLayout.tsx
// Enhanced SEO with Sun Light Meditation keywords and lead generation CTAs
// Optimized for macOS 11 and mobile responsiveness (iPhone 12, 390x844)
// 06/04/25 04:37 PM +07

import React, { useState, useEffect, useCallback } from "react";
import { Layout, Button, Typography, Tooltip } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { publicImages } from "../../../auth/constants/PublicImg";
import logo from "/logo.jpg";
import "./OLSaeo.css";

// Define the type for the dimensions state
interface Dimensions {
  width: number;
  height: number;
}

const { Title, Paragraph } = Typography;

const OLSaeo: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Layout
      className="full-height-layout"
      style={{
        background: "#000000",
        maxWidth: dimensions.width > 768 ? "1200px" : "100%",
        margin: "0 auto",
      }}
    >
      <Helmet>
        <title>OLS iee.aeo | Sun Light Meditation & Research Academy</title>
        <meta
          name="description"
          content="Join OLS iee.aeo at OneLightSystem OLS to explore Sun Light Meditation, scientific research, and counseling education. Discover innovative programs for sustainable communities and personal growth. Register at olsme.com/OLSStudentProspect!"
        />
        <meta
          name="keywords"
          content="OLS iee.aeo, Sun Light Meditation, scientific research, counseling education, sustainable communities, OLSME, Venus Mission, time synchronization, OneLightSystem OLS, personal growth, meditation academy, spiritual development, environmental research, community wellness, planetary well-being, Atetaten architecture, OLS counseling, OLS developer, OLS promoter, OLS referral program"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS iee.aeo | Sun Light Meditation & Research Academy" />
        <meta
          property="og:description"
          content="Explore OLS iee.aeo’s Sun Light Meditation, research, and counseling programs at OneLightSystem OLS. Join innovative initiatives for sustainable communities and personal growth. Register at olsme.com/OLSStudentProspect."
        />
        <meta property="og:url" content="https://www.olsme.com/OLSaeo" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS iee.aeo | Sun Light Meditation & Research Academy" />
        <meta
          name="twitter:description"
          content="Join OLS iee.aeo for Sun Light Meditation, research, and counseling at OneLightSystem OLS. Discover programs for sustainable communities and personal growth. Register at olsme.com/OLSStudentProspect."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "EducationalOrganization",
              "name": "OLS iee.aeo Undergraduate Department",
              "url": "https://www.olsme.com/OLSaeo",
              "description": "OLS iee.aeo at OneLightSystem OLS Education Academy offers undergraduate programs in Sun Light Meditation, scientific research, and counseling education for sustainable communities.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Belmont",
                "addressRegion": "CA",
                "addressCountry": "US"
              },
              "subOrganization": {
                "@type": "EducationalOrganization",
                "name": "OneLightSystem OLS",
                "url": "https://www.olsme.com"
              },
              "hasCourse": [
                {
                  "@type": "Course",
                  "name": "Sun Light Meditation Research",
                  "description": "Explore scientific research and solutions through Sun Light Meditation.",
                  "courseCode": "SLMR101",
                  "inLanguage": "en",
                  "image": "https://www.olsme.com/logo.jpg",
                  "provider": {
                    "@type": "EducationalOrganization",
                    "name": "OLS iee.aeo",
                    "url": "https://www.olsme.com/OLSaeo"
                  },
                  "offers": {
                    "@type": "Offer",
                    "category": "Meditation Education",
                    "price": "0",
                    "priceCurrency": "USD",
                    "availability": "https://schema.org/OnlineOnly"
                  },
                  "hasCourseInstance": {
                    "@type": "CourseInstance",
                    "courseMode": "online",
                    "courseSchedule": {
                      "@type": "Schedule",
                      "duration": "PT1H",
                      "repeatFrequency": "Daily",
                      "repeatCount": 365
                    }
                  }
                },
                {
                  "@type": "Course",
                  "name": "Counseling Education",
                  "description": "Learn implementive counseling with Sun Light Meditation techniques.",
                  "courseCode": "CEDU201",
                  "inLanguage": "en",
                  "image": "https://www.olsme.com/logo.jpg",
                  "provider": {
                    "@type": "EducationalOrganization",
                    "name": "OLS iee.aeo",
                    "url": "https://www.olsme.com/OLSaeo"
                  },
                  "offers": {
                    "@type": "Offer",
                    "category": "Counseling Education",
                    "price": "0",
                    "priceCurrency": "USD",
                    "availability": "https://schema.org/OnlineOnly"
                  },
                  "hasCourseInstance": {
                    "@type": "CourseInstance",
                    "courseMode": "online",
                    "courseSchedule": {
                      "@type": "Schedule",
                      "duration": "PT1H",
                      "repeatFrequency": "Daily",
                      "repeatCount": 365
                    }
                  }
                }
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "email": "contact@olsme.com"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "Event",
              "name": "OLS iee.aeo Community Wellness Initiatives",
              "description": "Ongoing community wellness programs by OLS iee.aeo, promoting sustainable communities through Sun Light Meditation and counseling.",
              "url": "https://www.olsme.com/OLSaeo",
              "organizer": {
                "@type": "EducationalOrganization",
                "name": "OLS iee.aeo",
                "url": "https://www.olsme.com/OLSaeo"
              },
              "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
              "eventStatus": "https://schema.org/EventScheduled",
              "startDate": "2025-06-04", // Updated to fix critical schema error
              "endDate": "2025-12-31",
              "offers": {
                "@type": "Offer",
                "category": "Community Wellness",
                "price": "0",
                "priceCurrency": "USD",
                "availability": "https://schema.org/OnlineOnly"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "OLS iee.aeo",
                  "item": "https://www.olsme.com/OLSaeo"
                }
              ]
            }
          ])}
        </script>
      </Helmet>
      <Layout.Header className="content-section header">
        <Tooltip title="Visit OLS Main Site">
          <Link to="/" onClick={handleLogoClick}>
            <img
              src={logo}
              alt="OneLightSystem OLS Education Academy Logo"
              className="logo"
              aria-label="OLS Logo"
              loading="lazy"
              width="150"
              height="50"
            />
          </Link>
        </Tooltip>
        <Title level={1} className="section-title" style={{ marginBottom: "20px" }}>
          OLS iee.aeo Science & Counseling
        </Title>
        <Paragraph className="content-text">
          OLS iee.aeo is an undergraduate science and counseling department <Link to="/OLSStudentProspect" aria-label="Join OLS Student Prospect">start OLS Student Prospect</Link>
        </Paragraph>
      </Layout.Header>

      <Layout.Content className="content-section">
        <div className="content">
          <Paragraph>
            <Link to="/lightScience" style={{ fontSize: "21px", color: "#ffaa00" }} aria-label="Visit OneLightSystem OLS Meditation Education Academy">
              OneLightSystem OLS Meditation Education Academy
            </Link>
          </Paragraph>
          <div className="button-row">
            <Button className="nav-button">
              <Link to="/lightScience" aria-label="Learn about OneLightSystem">OneLightSystem</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSinventions" aria-label="Discover OLS inventions">OLS Inventions</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/olsme" aria-label="Explore OLSME">OLSME</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLShealth" aria-label="Learn about OLS Health">OLS Health</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/Stat" aria-label="View OLS student statistics">OLS Student</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSStudentProspect" aria-label="Join OLS Student Prospect">Join OLS</Link>
            </Button>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              OLS iee (iense) - Science Department
            </Title>
            <Paragraph className="content-text">
              OLS iee (iense) drives scientific innovation to support communities and governing bodies with advanced research and sustainable solutions:
              <ul className="styled-list">
                <li>
                  <strong>Analytics & Research:</strong> Delivering data-driven insights for planetary well-being and sustainable development.
                </li>
                <li>
                  <strong>OneLightSystem Light Science:</strong> Pioneering Inner Equity Formation (IEF) to empower students, fostering personal illumination through Sun Light Meditation (OLSLM). Our labs offer hands-on research in:
                  <ul className="styled-list">
                    <li><strong>Local Solutions:</strong> Analyzing vibrations for tailored community improvements via OLSM meditation.</li>
                    <li>
                      <Button className="custom-button">
                        <Link to="/OLShealth" aria-label="Explore OLS Health initiatives">OLS Health</Link>
                      </Button> Advancing vitality and lifespan through unique self-cleansing methods.
                    </li>
                    <li><strong>Environmental Research:</strong> Studying aqua, sound, and light for eco-sustainability.</li>
                    <li>
                      <strong>Governance Collaboration:</strong> Partnering with state and federal entities to inform policy with real-time OLS Data Center insights.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Innovative Education:</strong> Equipping students with cutting-edge tools for research and continuous meditation practice. <Link to="/OLSStudentProspect" aria-label="Join OLS Student Prospect">Start your journey now!</Link>
                </li>
              </ul>
              <img
                src={publicImages.getImageById(12).url}
                alt="OLS Science Research Icon"
                className="section-icon"
                loading="lazy"
                width="100"
                height="100"
              />
              <Button className="custom-button">
                <Link to="/Stat" aria-label="View OLS student statistics">OLS Student</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              Time and Calendar Synchronization
            </Title>
            <Paragraph className="content-text">
              <ul className="styled-list">
                <li>
                  <strong>Enhanced Time Measurement:</strong> OLS iee offers an improved calendar system—available to all. Request access today.
                </li>
                <li><strong>OLSLM Technique:</strong> Practice "Head on the Plate" meditation for personal alignment.</li>
              </ul>
              <Button className="custom-button">
                <Link to="/OLSinventions" aria-label="Discover OLS Inventions">Explore OLS Inventions</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              Natural Intelligence with OLSME
            </Title>
            <img
              src={publicImages.getImageById(9).url}
              alt="OLSME Natural Intelligence Icon"
              className="section-icon"
              loading="lazy"
              width="100"
              height="100"
            />
            <Paragraph className="content-text">
              <ul className="styled-list">
                <li>
                  <strong><Link to="/olsme" aria-label="Learn about OLSME">OLSME:</Link></strong> Unlock and connect to meditative energy with OLSME, syncing you with Source Energy through Sun Light Meditation for personal growth.
                </li>
              </ul>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              Venus Mission
            </Title>
            <Paragraph className="content-text">
              <ul className="styled-list">
                <li>
                  <strong>Since OLS.1847:</strong> Pioneering Venus settlement research, adapting OLS techniques for sustainable interplanetary living.
                </li>
              </ul>
              <Button className="custom-button">
                <Link to="/venus" aria-label="Learn about Venus Mission">Explore Venus Mission</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              Establishing Sustainable Communities
            </Title>
            <Paragraph className="content-text">
              <ul className="styled-list">
                <li>
                  <strong>Atetaten Architecture:</strong> Eco-friendly, light-optimized designs for resilient communities.
                </li>
                <li>
                  <strong>Farming Science:</strong> Sustainable agriculture enhanced by Sun Light Meditation for better yields.
                </li>
                <li>
                  <strong>Global Research:</strong> Addressing climate change, resources, and equity through ongoing analysis.
                </li>
              </ul>
              <Button className="custom-button">
                <Link to="/contact" aria-label="Contact OLS for community initiatives">Contact Us</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              OLS aeo (aunselo) - Implementive Counseling
            </Title>
            <Paragraph className="content-text">
              OLS aeo (aunselo) transforms communities and organizations through practical, light-based counseling:
              <ul className="styled-list">
                <li><strong>Community Impact:</strong> Implementing OLS iense solutions for sustainable growth.</li>
                <li><strong>Policy Integration:</strong> Advising governmental bodies to embed meditation in decision-making.</li>
                <li><strong>Corporate Wellness:</strong> Offering OLSME training to enhance workplace vitality with Sun Light Meditation.</li>
              </ul>
              <Button className="custom-button">
                <Link to="/contact" aria-label="Start OLS counseling inquiry">Start Counseling Inquiry</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={2} className="subsection-title">
              OLS Developer and Promoter Opportunities
            </Title>
            <Paragraph className="content-text">
              <ul className="styled-list">
                <li>
                  <strong>OLS Developer:</strong> Our team continues to develop <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer">olsme.com</a>, creating innovative features like timer sessions and new widgets. Support our efforts by becoming a Git sponsor at <a href="https://github.com/sponsors/onelightsystem" target="_blank" rel="noopener noreferrer">GitHub Sponsors</a>.
                </li>
                <li>
                  <strong>OLS Promoter:</strong> Join our referral program and earn a 5% commission for each successful student you refer. Start this unique earning opportunity at <Link to="/referral" aria-label="Join OLS Referral Program">OLS Referral Program</Link>.
                </li>
              </ul>
              <Button className="custom-button">
                <Link to="/referral" aria-label="Join OLS Promoter Referral">Become an OLS Affiliate</Link>
              </Button>
              <Button className="custom-button">
                <Link to="/OLSStudentProspect" aria-label="Join OLS Student Prospect">Join OLS Community</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="button-group">
            <Tooltip title="OLS Student Registration">
              <Button className="custom-button">
                <Link to="/register/olssregister" aria-label="Register as OLS Student">OLS Student</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSME Subscription">
              <Button className="custom-button">
                <Link to="/register/olsmeregister" aria-label="Subscribe to OLSME">OLSME Subscription</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSLM Assets">
              <Button className="custom-button">
                <Link to="/register/olslmregister" aria-label="Explore OLSLM Assets">OLSLM Assets</Link>
              </Button>
            </Tooltip>
            <Tooltip title="Join OLS Referral Program">
              <Button className="custom-button">
                <Link to="/referral" aria-label="Join OLS Referral Program">Join Referral Program</Link>
              </Button>
            </Tooltip>
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default OLSaeo;